import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveFarmer } from '../siteFarmer';
import { fetchSite, getToken, patchFarmer, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

const cardClass = 'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const ghostBtnClass =
  'rounded-lg px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100';

function ModalFooter({ onCancel, onApply, applyLabel = 'Apply', applyDisabled = false }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} disabled={applyDisabled} className={primaryBtnClass}>
        {applyLabel}
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

export default function FarmerDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => resolveFarmer(DEFAULT_SITE));
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadKey, setUploadKey] = useState('');
  const [modalErr, setModalErr] = useState('');

  const [heroOpen, setHeroOpen] = useState(false);
  const [heroDraft, setHeroDraft] = useState(null);

  const [storyOpen, setStoryOpen] = useState(false);
  const [storyDraft, setStoryDraft] = useState(null);

  const [closingOpen, setClosingOpen] = useState(false);
  const [closingDraft, setClosingDraft] = useState(null);

  const [bulletOpen, setBulletOpen] = useState(false);
  const [bulletDraft, setBulletDraft] = useState('');
  const [bulletIndex, setBulletIndex] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setForm(resolveFarmer(site)))
      .catch(() => setForm(resolveFarmer(DEFAULT_SITE)));
  }, [navigate]);

  function openHeroModal() {
    setModalErr('');
    setHeroDraft({
      pageHeroImage: form.pageHeroImage || '',
      heroTitle: form.heroTitle || '',
      heroBreadcrumbHome: form.heroBreadcrumbHome || '',
      heroBreadcrumbCurrent: form.heroBreadcrumbCurrent || '',
    });
    setHeroOpen(true);
  }

  function applyHeroModal() {
    if (!heroDraft) return;
    setForm((f) => ({
      ...f,
      pageHeroImage: heroDraft.pageHeroImage || '',
      heroTitle: heroDraft.heroTitle || '',
      heroBreadcrumbHome: heroDraft.heroBreadcrumbHome || '',
      heroBreadcrumbCurrent: heroDraft.heroBreadcrumbCurrent || '',
    }));
    setHeroOpen(false);
    setHeroDraft(null);
    setModalErr('');
  }

  function openStoryModal() {
    setModalErr('');
    const st = form.story || {};
    setStoryDraft({
      badge: st.badge || '',
      eyebrow: st.eyebrow || '',
      title: st.title || '',
      body: st.body || '',
      image: st.image || '',
      imageAlt: st.imageAlt || '',
      caption: st.caption || '',
    });
    setStoryOpen(true);
  }

  function applyStoryModal() {
    if (!storyDraft) return;
    setForm((f) => ({
      ...f,
      story: {
        ...f.story,
        badge: storyDraft.badge || '',
        eyebrow: storyDraft.eyebrow || '',
        title: storyDraft.title || '',
        body: storyDraft.body || '',
        image: storyDraft.image || '',
        imageAlt: storyDraft.imageAlt || '',
        caption: storyDraft.caption || '',
        bullets: Array.isArray(f.story?.bullets) ? f.story.bullets : [],
      },
    }));
    setStoryOpen(false);
    setStoryDraft(null);
    setModalErr('');
  }

  function openClosingModal() {
    setModalErr('');
    const c = form.closing || {};
    setClosingDraft({
      eyebrow: c.eyebrow || '',
      title: c.title || '',
      body: c.body || '',
    });
    setClosingOpen(true);
  }

  function applyClosingModal() {
    if (!closingDraft) return;
    setForm((f) => ({
      ...f,
      closing: {
        eyebrow: closingDraft.eyebrow || '',
        title: closingDraft.title || '',
        body: closingDraft.body || '',
      },
    }));
    setClosingOpen(false);
    setClosingDraft(null);
    setModalErr('');
  }

  function openBulletModal(index) {
    setModalErr('');
    const bullets = Array.isArray(form.story?.bullets) ? [...form.story.bullets] : [];
    setBulletIndex(index);
    setBulletDraft(index == null ? '' : typeof bullets[index] === 'string' ? bullets[index] : '');
    setBulletOpen(true);
  }

  function applyBulletModal() {
    const text = typeof bulletDraft === 'string' ? bulletDraft.trim() : '';
    if (!text) {
      setModalErr('Enter bullet text.');
      return;
    }
    setForm((f) => {
      const prev = Array.isArray(f.story?.bullets) ? [...f.story.bullets] : [];
      if (bulletIndex == null) {
        prev.push(text);
      } else {
        prev[bulletIndex] = text;
      }
      return { ...f, story: { ...f.story, bullets: prev } };
    });
    setBulletOpen(false);
    setBulletDraft('');
    setBulletIndex(null);
    setModalErr('');
  }

  function removeBullet(i) {
    if (!window.confirm('Remove this bullet?')) return;
    setForm((f) => ({
      ...f,
      story: {
        ...f.story,
        bullets: (f.story?.bullets || []).filter((_, j) => j !== i),
      },
    }));
  }

  async function runUpload(file, applyUrl) {
    if (!file) return;
    setErr('');
    setUploadKey('busy');
    try {
      const { url } = await uploadFile(file);
      applyUrl(url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadKey('');
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    const bullets = (form.story?.bullets || [])
      .map((b) => (typeof b === 'string' ? b.trim() : ''))
      .filter(Boolean);
    setSaving(true);
    try {
      const payload = {
        pageHeroImage: form.pageHeroImage || '',
        heroTitle: form.heroTitle || '',
        heroBreadcrumbHome: form.heroBreadcrumbHome || '',
        heroBreadcrumbCurrent: form.heroBreadcrumbCurrent || '',
        story: {
          ...form.story,
          bullets,
        },
        closing: {
          ...form.closing,
        },
      };
      const { farmer } = await patchFarmer(payload);
      setForm(resolveFarmer({ farmer: farmer || payload }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const uploading = uploadKey === 'busy';
  const bullets = Array.isArray(form.story?.bullets) ? form.story.bullets : [];

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-neutral-900">Farmer story page</h1>
          <p className="text-sm text-neutral-600">Edit content in dialogs; save when you are ready to publish.</p>
        </div>
        <button type="button" disabled={saving} onClick={save} className={primaryBtnClass}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{msg}</p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="space-y-4">
          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Hero</h2>
                <p className="mt-1 text-sm text-neutral-600">
                  {clip(form.heroTitle, 48)} · {clip(form.heroBreadcrumbHome, 12)} / {clip(form.heroBreadcrumbCurrent, 16)}
                </p>
              </div>
              <button type="button" onClick={openHeroModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
            {form.pageHeroImage ? (
              <div className="mt-4 max-w-xs">
                <AdminImagePreview src={form.pageHeroImage} alt="" emptyLabel="No hero image" />
              </div>
            ) : null}
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Story section</h2>
                <p className="mt-1 text-sm text-neutral-600">{clip(form.story?.title, 60)}</p>
              </div>
              <button type="button" onClick={openStoryModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
            <p className="mt-3 text-xs text-neutral-500">{bullets.length} bullet{bullets.length === 1 ? '' : 's'}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => openBulletModal(null)} className={secondaryBtnClass}>
                Add bullet
              </button>
            </div>
            {bullets.length ? (
              <ul className="mt-3 list-none space-y-2 p-0">
                {bullets.map((b, i) => (
                  <li
                    key={`b-${i}-${String(b).slice(0, 24)}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 text-neutral-800">{clip(b, 120)}</span>
                    <span className="flex shrink-0 gap-1">
                      <button type="button" className={ghostBtnClass} onClick={() => openBulletModal(i)}>
                        Edit
                      </button>
                      <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => removeBullet(i)}>
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-neutral-500">No bullets yet. Use Add bullet.</p>
            )}
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Closing band</h2>
                <p className="mt-1 text-sm text-neutral-600">{clip(form.closing?.title, 56)}</p>
              </div>
              <button type="button" onClick={openClosingModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
          </section>
        </div>
      </div>

      <AdminModal
        open={heroOpen}
        title="Hero"
        description="Page hero image, title, and breadcrumb labels."
        error={modalErr}
        onClose={() => {
          setHeroOpen(false);
          setHeroDraft(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setHeroOpen(false);
              setHeroDraft(null);
              setModalErr('');
            }}
            onApply={applyHeroModal}
          />
        }
      >
        {heroDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={`${label} md:col-span-2`}>
              Page hero image URL
              <input
                className={input}
                value={heroDraft.pageHeroImage}
                onChange={(e) => setHeroDraft((d) => ({ ...d, pageHeroImage: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Upload hero image
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  runUpload(f, (url) => setHeroDraft((d) => ({ ...d, pageHeroImage: url })));
                }}
              />
            </label>
            <AdminImagePreview src={heroDraft.pageHeroImage} alt="" />
            <label className={`${label} md:col-span-2`}>
              Title (H1)
              <input
                className={input}
                value={heroDraft.heroTitle}
                onChange={(e) => setHeroDraft((d) => ({ ...d, heroTitle: e.target.value }))}
              />
            </label>
            <label className={label}>
              Breadcrumb — home label
              <input
                className={input}
                value={heroDraft.heroBreadcrumbHome}
                onChange={(e) => setHeroDraft((d) => ({ ...d, heroBreadcrumbHome: e.target.value }))}
              />
            </label>
            <label className={label}>
              Breadcrumb — current
              <input
                className={input}
                value={heroDraft.heroBreadcrumbCurrent}
                onChange={(e) => setHeroDraft((d) => ({ ...d, heroBreadcrumbCurrent: e.target.value }))}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={storyOpen}
        title="Story section"
        description="Eyebrow, title, body, image, and caption (bullets are edited separately)."
        error={modalErr}
        onClose={() => {
          setStoryOpen(false);
          setStoryDraft(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setStoryOpen(false);
              setStoryDraft(null);
              setModalErr('');
            }}
            onApply={applyStoryModal}
          />
        }
      >
        {storyDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Image badge
              <input
                className={input}
                value={storyDraft.badge}
                onChange={(e) => setStoryDraft((d) => ({ ...d, badge: e.target.value }))}
              />
            </label>
            <label className={label}>
              Eyebrow
              <input
                className={input}
                value={storyDraft.eyebrow}
                onChange={(e) => setStoryDraft((d) => ({ ...d, eyebrow: e.target.value }))}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={storyDraft.title}
                onChange={(e) => setStoryDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Body
              <textarea
                className={`${input} min-h-[6rem]`}
                value={storyDraft.body}
                onChange={(e) => setStoryDraft((d) => ({ ...d, body: e.target.value }))}
              />
            </label>
            <label className={label}>
              Image URL
              <input
                className={input}
                value={storyDraft.image}
                onChange={(e) => setStoryDraft((d) => ({ ...d, image: e.target.value }))}
              />
            </label>
            <label className={label}>
              Upload image
              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  runUpload(f, (url) => setStoryDraft((d) => ({ ...d, image: url })));
                }}
              />
            </label>
            <label className={label}>
              Image alt
              <input
                className={input}
                value={storyDraft.imageAlt}
                onChange={(e) => setStoryDraft((d) => ({ ...d, imageAlt: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Image caption
              <input
                className={input}
                value={storyDraft.caption}
                onChange={(e) => setStoryDraft((d) => ({ ...d, caption: e.target.value }))}
              />
            </label>
            <div className="md:col-span-2">
              <AdminImagePreview src={storyDraft.image} alt={storyDraft.imageAlt || ''} />
            </div>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={closingOpen}
        title="Closing band"
        description="Dark section at the bottom: eyebrow, title, and body."
        error={modalErr}
        onClose={() => {
          setClosingOpen(false);
          setClosingDraft(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setClosingOpen(false);
              setClosingDraft(null);
              setModalErr('');
            }}
            onApply={applyClosingModal}
          />
        }
      >
        {closingDraft ? (
          <div className="grid gap-4">
            <label className={label}>
              Eyebrow
              <input
                className={input}
                value={closingDraft.eyebrow}
                onChange={(e) => setClosingDraft((d) => ({ ...d, eyebrow: e.target.value }))}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={closingDraft.title}
                onChange={(e) => setClosingDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </label>
            <label className={label}>
              Body
              <textarea
                className={`${input} min-h-[6rem]`}
                value={closingDraft.body}
                onChange={(e) => setClosingDraft((d) => ({ ...d, body: e.target.value }))}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={bulletOpen}
        title={bulletIndex == null ? 'Add bullet' : 'Edit bullet'}
        description="One line of supporting copy for the story section."
        error={modalErr}
        onClose={() => {
          setBulletOpen(false);
          setBulletDraft('');
          setBulletIndex(null);
          setModalErr('');
        }}
        footer={
          <ModalFooter
            onCancel={() => {
              setBulletOpen(false);
              setBulletDraft('');
              setBulletIndex(null);
              setModalErr('');
            }}
            onApply={applyBulletModal}
          />
        }
      >
        <label className={label}>
          Bullet text
          <textarea
            className={`${input} min-h-[5rem]`}
            value={bulletDraft}
            onChange={(e) => {
              setBulletDraft(e.target.value);
              setModalErr('');
            }}
            placeholder="e.g. Demo plots and on-farm visits before you commit."
          />
        </label>
      </AdminModal>
    </>
  );
}
